import React, { useState, useEffect } from 'react';
import PageTitle from '../shared/PageTitle';
import { useSettings } from '../../contexts/SettingsContext';
import { noticeAPI } from '../../services/api';

// Generate dynamic styles for maintenance announcements
const generateAnnouncementsStyles = (settings) => `
  .maintenance-announcements .card-header {
    background-color: ${settings.block_title_bg_color || '#f8f9fa'} !important;
    color: ${settings.block_title_color || '#212529'} !important;
  }
  
  .maintenance-announcements .card-header h5 {
    color: ${settings.block_title_color || '#212529'} !important;
    margin: 0 !important;
  }
  
  .maintenance-announcements .card {
    background-color: ${settings.card_bg_color || '#ffffff'} !important;
  }
  
  .maintenance-announcements .card-body {
    background-color: ${settings.card_bg_color || '#ffffff'} !important;
    color: ${settings.text_color || '#212529'} !important;
  }
  
  /* .maintenance-announcements .alert-info {
    background-color: ${settings.btn_info_bg || '#d1ecf1'} !important;
    color: ${settings.btn_info_text || '#0c5460'} !important;
    border-color: ${settings.btn_info_bg || '#bee5eb'} !important;
  } */
  
  .maintenance-announcements .list-group-item {
    background-color: ${settings.card_bg_color || '#ffffff'} !important;
    color: ${settings.text_color || '#212529'} !important;
    border-color: rgba(0, 0, 0, 0.125) !important;
  }
`;

const MaintenanceAnnouncements = () => {
  const { settings } = useSettings();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Inject custom styles for maintenance announcements
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.id = 'maintenance-announcements-styles';
    styleElement.innerHTML = generateAnnouncementsStyles(settings);
    document.head.appendChild(styleElement);

    return () => {
      const existingStyle = document.getElementById('maintenance-announcements-styles');
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, [settings]);

  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        const response = await noticeAPI.getAnnouncements();
        setAnnouncements(response.data);
      } catch (err) {
        console.error('Error fetching announcements:', err);
        setError('Failed to load announcements.');
      } finally {
        setLoading(false);
      }
    };
    fetchAnnouncements();
  }, []);

  const isAnnouncementActive = (announcement) => {
    const now = new Date();
    const startDate = new Date(announcement.start_date);
    const expiryDate = announcement.expiry_date ? new Date(announcement.expiry_date) : null;
    
    return startDate <= now && (!expiryDate || expiryDate >= now);
  };

  const getAnnouncementStatus = (announcement) => {
    const now = new Date();
    const startDate = new Date(announcement.start_date);
    const expiryDate = announcement.expiry_date ? new Date(announcement.expiry_date) : null;
    
    if (startDate > now) {
      return { status: 'upcoming', badge: 'bg-warning text-dark', text: 'Upcoming' };
    } else if (expiryDate && expiryDate < now) {
      return { status: 'expired', badge: 'bg-secondary', text: 'Expired' };
    } else {
      return { status: 'active', badge: 'bg-success', text: 'Active' };
    }
  };

  if (loading) {
    return (
      <div className="maintenance-announcements container-fluid mt-4">
        <PageTitle icon="fa-bullhorn" title="Announcements" />
        <div className="d-flex justify-content-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="maintenance-announcements container-fluid mt-4">
        <PageTitle icon="fa-bullhorn" title="Announcements" />
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error}
        </div>
      </div>
    );
  }

  const activeAnnouncements = announcements.filter(isAnnouncementActive);
  const upcomingExpiredAnnouncements = announcements.filter(ann => !isAnnouncementActive(ann));

  return (
    <div className="maintenance-announcements container-fluid mt-4">
      <PageTitle icon="fa-bullhorn" title="Society Announcements" />
      
      {announcements.length === 0 ? (
        <div className="alert alert-info">
          <i className="fas fa-info-circle me-2"></i>
          No announcements available at this time.
        </div>
      ) : (
        <>
          {/* Active Announcements */}
          {activeAnnouncements.length > 0 && (
            <div className="mb-4">
              <h4 className="text-success mb-3">
                <i className="fas fa-check-circle me-2"></i>
                Current Announcements
              </h4>
              <div className="row">
                {activeAnnouncements.map((ann) => {
                  const statusInfo = getAnnouncementStatus(ann);
                  return (
                    <div key={ann.id} className="col-lg-6 col-xl-4 mb-3">
                      <div className="card h-100 shadow-sm border-success">
                        <div className="card-header">
                          <div className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-0 fw-bold">{ann.title}</h6>
                            <span className={`badge ${statusInfo.badge} ms-2`}>
                              {statusInfo.text}
                            </span>
                          </div>
                        </div>
                        <div className="card-body">
                          <p className="card-text">{ann.description}</p>
                          <div className="mt-3">
                            <small className="text-muted">
                              <i className="fas fa-calendar-alt me-2"></i>
                              <strong>Valid:</strong> {new Date(ann.start_date).toLocaleDateString()}
                              {ann.expiry_date && (
                                <span> - {new Date(ann.expiry_date).toLocaleDateString()}</span>
                              )}
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Upcoming/Expired Announcements */}
          {upcomingExpiredAnnouncements.length > 0 && (
            <div className="mb-4">
              <h4 className="text-muted mb-3">
                <i className="fas fa-archive me-2"></i>
                Other Announcements
              </h4>
              <div className="row">
                {upcomingExpiredAnnouncements.map((ann) => {
                  const statusInfo = getAnnouncementStatus(ann);
                  return (
                    <div key={ann.id} className="col-lg-6 col-xl-4 mb-3">
                      <div className="card h-100 shadow-sm">
                        <div className="card-header bg-light">
                          <div className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-0 text-dark">{ann.title}</h6>
                            <span className={`badge ${statusInfo.badge} ms-2`}>
                              {statusInfo.text}
                            </span>
                          </div>
                        </div>
                        <div className="card-body">
                          <p className="card-text text-muted">{ann.description}</p>
                          <div className="mt-3">
                            <small className="text-muted">
                              <i className="fas fa-calendar-alt me-2"></i>
                              <strong>Period:</strong> {new Date(ann.start_date).toLocaleDateString()}
                              {ann.expiry_date && (
                                <span> - {new Date(ann.expiry_date).toLocaleDateString()}</span>
                              )}
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MaintenanceAnnouncements; 